import { useCallback, useEffect, useState, VFC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { doc, getDoc } from 'firebase/firestore';
import HTMLReactParser from 'html-react-parser';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  IconButton,
  Typography,
} from '@material-ui/core';
import { auth, db, FirebaseTimestamp } from '../firebase';
import { ImageSwiper } from '../components/Products/ImageSwiper';
import { RootState } from '../reducks/store/store';
import { PrimaryOutlineButton } from '../components/Uikit/PrimaryOutlineButton';
import { onAuthStateChanged } from 'firebase/auth';
import { signInAction } from '../reducks/users/actions';
import { ListItem, ListItemText } from '@material-ui/core';
import AddCircle from '@material-ui/icons/AddCircle';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import { getProductsInCart, getRole } from '../reducks/users/selectors';
import {
  addProductToCart, subtractProductFromCart
} from '../reducks/users/operations';
import { ProductInCart } from '../reducks/users/types';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  slideBox: {
    // スマートフォンサイズ
    [theme.breakpoints.down('sm')]: {
      margin: '0 auto 24px auto',
      // iphoneSEの幅に合わせて、スライドボックスのサイズを調整
      height: 320,
      width: 320,
    },
    [theme.breakpoints.up('sm')]: {
      margin: '0 auto',
      height: 400,
      width: 400,
    },
  },
  detail: {
    textAlign: 'left',
    // スマートフォンサイズ
    [theme.breakpoints.down('sm')]: {
      margin: '0 auto 16px auto',
      height: 'auto',
      width: 320,
    },
    [theme.breakpoints.up('sm')]: {
      margin: '0 auto',
      height: 'auto',
      width: 400,
    },
  },
  price: {
    fontSize: 36,
  },
  quantityText: {
    width: '50px',
    textAlign: 'center',
  },
}));

/**
 * テキストをHTMLに変換.
 *
 * @remarks
 * 改行はbrタグに変換する。
 *
 * @param text - HTMLに変換するテキスト
 * @returns textが空文字列の場合は空文字列。そうでなければHTMLに変換した文字列。
 */
const returnCodeToBr = (text: string) => {
  if (text === '') {
    return text;
  } else {
    return HTMLReactParser(text.replace(/\r?\n/g, '<br />'));
  }
};

const ProductDetail: VFC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const selector = useSelector((state: RootState) => state);
  const cart = getProductsInCart(selector);
  const navigate = useNavigate();

  const [quantity, setQuantity] = useState<number>(0);

  const receiveDateParts = selector.global.receiveDate ?? new Date();
  const year = receiveDateParts.getFullYear();
  const month = ("00" + (receiveDateParts.getMonth() + 1)).slice(-2);
  const day = ("00" + receiveDateParts.getDate()).slice(-2);
  const yyyymmdd = year + month + day;

  const role = getRole(selector);

  // 表示する商品
  const [product, setProduct] = useState<any>(null);

  useEffect(() => {
    if(!product) return;
    const num = cart.find((productInCart: ProductInCart) => (productInCart.id === product.id));
    if(num === undefined)return;
    setQuantity(num.quantity);
  }, [cart, product]);

  // マウント時、IDで商品情報を取得
  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        // ユーザーがログインしている場合
        const uid = user.uid;
        // ユーザーの情報を取得
        getDoc(doc(db, 'users', uid)).then((snapshot) => {
          const userData = snapshot.data();
          if (userData) {
            // ストアの中身を更新
            dispatch(
              signInAction({
                address: userData.address,
                birthdayYear: userData.birthdayYear,
                birthdayMonth: userData.birthdayMonth,
                cart: userData.cart,
                email: userData.email,
                firstNameKana: userData.firstNameKana,
                gender: userData.gender,
                isSignedIn: true,
                lastNameKana: userData.lastNameKana,
                orders: userData.orders,
                phone: userData.phone,
                role: userData.role,
                uid: uid,
              })
            );
          }
        });
      }
    });
    let id = window.location.pathname.split('/product/detail')[1];
    if (id !== '') {
      id = id.split('/')[1];
    }
    // URLから商品のIDを取得
    getDoc(doc(db, 'products', id)).then((snapshot) => {
      const data = snapshot.data();
      setProduct(data);
    });
  }, [dispatch]);

  /**
   * 商品をカートに追加する.
   *
   * @param selectedSize - カートに追加する商品のサイズ
   */
  const addProductQuantity = async () => {
    const timestamp = FirebaseTimestamp.now();

    if(!product.stocks[yyyymmdd])return;

    if(!product.ordered || !product.ordered[yyyymmdd]){
      if(product.stocks[yyyymmdd] - quantity <= 0) return;
    }else{
        if(product.stocks[yyyymmdd] - product.ordered[yyyymmdd] - quantity <= 0) return;
    }
    dispatch(
      addProductToCart(
        {
          ...product,
          added_at: timestamp,
        },
        cart
      )
    );
    setQuantity(quantity + 1);
  };

  const subtractProductQuantity = async () => {
    const timestamp = FirebaseTimestamp.now();

    dispatch(
      subtractProductFromCart(
        {
          ...product,
          added_at: timestamp,
        },
        cart
      )
    );
    if(quantity > 0) setQuantity(quantity - 1);
  };

  const backToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  return (
    <section className='mx-auto my-0 max-w-xl relative py-0 px-4 text-center w-full sm:max-w-5xl'>
      {product && (
        <div className='flex flex-row flex-wrap lg:mt-24'>
          <div className={classes.slideBox}>
            <ImageSwiper images={product.images} />
          </div>
          <div className={classes.detail}>
            <h2 className='text-pandacco text-2xl mt-0 mx-auto mb-4'>
              {returnCodeToBr(product.name)}
            </h2>
            <p className={classes.price}>¥{product.price.toLocaleString()}</p>
            <div className='h-5 sm:h-8'></div>

            <div>
          {(product.stocks[yyyymmdd] - product.ordered[yyyymmdd] <= 0 || !product.stocks[yyyymmdd]) && (
            <Typography color="error">在庫なし</Typography>
          )}
          {(role !== 'admin') && (
            <ListItem>
              <ListItemText
                primary={`注文数`}
              />
              <IconButton onClick={subtractProductQuantity}>
                <RemoveCircle />
              </IconButton>
              <div className={classes.quantityText}>
                <ListItemText
                  primary={`${quantity}`}
                />
              </div>
              <IconButton onClick={addProductQuantity}>
                <AddCircle />
              </IconButton>
              {product.stocks[yyyymmdd] - product.ordered[yyyymmdd] <= 5 ? <Typography color="error">{"残り："+(product.stocks[yyyymmdd] - product.ordered[yyyymmdd])+"点"}</Typography> : <></>}
            </ListItem>)
          }
          </div>
            <div className='h-5 sm:h-8'></div>
            <p>{returnCodeToBr(product.description)}</p>
            <br/><br/>
          </div>
        </div>
      )}
      {/* <Modal isOpen={isOpen} closeModal={closeModal} text={modalText} /> */}
      <div className='items-center flex flex-col'>
        <div className='h-3 sm:h-4' />
        <PrimaryOutlineButton label={'注文を続ける'} onClick={backToHome} />
      </div>
    </section>
  );
};

export default ProductDetail;
